/*
 * Cart drawer — the Liquid port of Hydrogen's cart Aside (app/components/Aside.tsx,
 * CartMain.tsx). Every add-to-cart form carrying [data-add-to-cart] posts to
 * /cart/add.js and opens the drawer; the drawer's own quantity, remove and
 * discount forms post to /cart/change.js and /cart/update.js. Each call asks
 * for the `cart-drawer` section through the Section Rendering API, so the
 * markup always comes from sections/cart-drawer.liquid.
 *
 * Without JS (or on /cart, where the drawer is not rendered) every form is a
 * plain POST that lands on the /cart page.
 */
(() => {
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const SECTION_ID = 'cart-drawer';
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const ADDED_LABEL_MS = 2000;

  // Keep preview/locale context. A failed section render must never replay an add.
  const pageContext = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    return url.pathname + url.search;
  };
  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  // Console breadcrumbs for support: every failed cart/section request is logged
  // with its URL and HTTP status, so a shopper's report can be traced.
  const logIssue = (step, detail) => {
    try { console.warn('[seed-cart]', step, detail); } catch (_) {}
  };
  // A non-JSON reply (rate-limit page, app interference) resolves with json: null
  // instead of throwing, so callers can tell "no reply" from "unusable reply".
  const fetchJson = async (url, options = {}) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
      const text = await response.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch (_) {
        logIssue('non-JSON reply', { url, status: response.status });
      }
      if (!response.ok) logIssue('HTTP error', { url, status: response.status, description: json && (json.description || json.message) });
      return { ok: response.ok && json !== null, status: response.status, json: json || {} };
    } finally {
      clearTimeout(timer);
    }
  };
  const cartPageUrl = () => `${ROOT}cart`;
  const postJson = (path, payload) => fetchJson(`${ROOT}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(payload),
  });

  class CartDrawer {
    constructor(element) {
      this.element = element;
      this.panel = element.querySelector('aside');
      this.opener = null;
      this.busy = false;
      this.revision = 0;
      this.refreshSequence = 0;
      this.lockedButtons = new Map();
      this.lockedExpress = new Map();
      this.upsellCache = new Map();
      this.headerButtons = [...document.querySelectorAll('.seed-cart-btn')];

      element.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
        button.addEventListener('click', () => this.close());
      });
      element.addEventListener('keydown', (event) => this.handleKeydown(event));
      element.addEventListener('submit', (event) => this.handleSubmit(event));

      this.headerButtons.forEach((button) => {
        button.setAttribute('aria-haspopup', 'dialog');
        button.setAttribute('aria-controls', element.id);
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(button);
          if (!this.busy) this.refreshQuietly();
        });
      });

      this.loadUpsells();
    }

    // Lock all form-associated buy buttons, including the sticky one. Preserve
    // pre-existing disabled states; never enable a sold-out button on completion.
    syncBusy() {
      if (!this.busy) return;
      const buttons = new Set(this.element.querySelectorAll('button[type="submit"], input[type="submit"]'));
      document.querySelectorAll('form[data-add-to-cart]').forEach((form) => {
        [...form.elements].forEach((control) => {
          if (control.type === 'submit') buttons.add(control);
        });
      });
      buttons.forEach((button) => {
        if (!this.lockedButtons.has(button)) {
          this.lockedButtons.set(button, { disabled: button.disabled, busy: button.getAttribute('aria-busy') });
        }
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      });
      this.element.querySelectorAll('.cart-express-checkout').forEach((element) => {
        if (!this.lockedExpress.has(element)) this.lockedExpress.set(element, element.inert);
        element.inert = true;
      });
      this.content?.setAttribute('aria-busy', 'true');
    }

    beginMutation() {
      if (this.busy) return false;
      this.busy = true;
      this.revision += 1;
      this.syncBusy();
      this.showError('');
      return true;
    }

    endMutation() {
      this.busy = false;
      this.lockedButtons.forEach((state, button) => {
        button.disabled = state.disabled;
        if (state.busy === null) button.removeAttribute('aria-busy');
        else button.setAttribute('aria-busy', state.busy);
      });
      this.lockedButtons.clear();
      this.lockedExpress.forEach((inert, element) => { element.inert = inert; });
      this.lockedExpress.clear();
      this.content?.removeAttribute('aria-busy');
    }

    // Opening the drawer only re-syncs it. If that fails the last rendered cart
    // stays and a link to the full cart page is offered, without an error text:
    // nothing the shopper did has failed.
    refreshQuietly() {
      return this.refreshWithRetry().then((ok) => {
        if (!ok) this.showCartLink();
        return ok;
      });
    }

    // The server confirmed the mutation but the drawer cannot be re-rendered:
    // send the shopper to the cart page, which always shows the true state,
    // instead of reporting a failure that did not happen.
    showConfirmedState() {
      logIssue('drawer re-render failed after a confirmed change; opening the cart page', {});
      window.location.assign(cartPageUrl());
    }

    showRecovery() {
      this.showError(this.element.dataset.networkError);
      this.showCartLink();
    }

    showCartLink() {
      let link = this.panel.querySelector('[data-cart-recovery]');
      if (!link) {
        link = document.createElement('a');
        link.dataset.cartRecovery = '';
        link.className = 'seed-btn seed-btn-outline seed-btn-size-lg';
        link.href = cartPageUrl();
        // Reuse the existing translated cart heading rather than hard-code a language.
        link.textContent = this.panel.querySelector('header h3')?.textContent || this.element.dataset.networkError;
        this.panel.append(link);
      }
      link.hidden = false;
    }

    get content() {
      return this.element.querySelector('[data-cart-drawer-content]');
    }

    get isOpen() {
      return this.element.classList.contains('expanded');
    }

    open(opener) {
      if (!this.isOpen) this.opener = opener || document.activeElement;
      this.element.classList.add('expanded');
      this.headerButtons.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      window.requestAnimationFrame(() => {
        const close = this.panel.querySelector('header [data-cart-drawer-close]');
        if (close && !this.panel.contains(document.activeElement)) close.focus();
      });
    }

    close() {
      if (!this.isOpen) return;
      this.element.classList.remove('expanded');
      this.headerButtons.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      if (this.opener && document.contains(this.opener)) this.opener.focus();
      this.opener = null;
    }

    handleKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...this.panel.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    showError(message) {
      const error = this.content?.querySelector('[data-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    }

    // Swaps in freshly rendered drawer markup, keeping focus on the control
    // the shopper was using (by its position among the drawer's controls).
    render(sectionHtml) {
      if (!sectionHtml) return false;
      const next = new DOMParser()
        .parseFromString(sectionHtml, 'text/html')
        .querySelector('[data-cart-drawer-content]');
      if (!next || !this.content) return false;

      const controls = [...this.content.querySelectorAll(FOCUSABLE)];
      const focusIndex = controls.indexOf(document.activeElement);
      // Keep the discount field open if the shopper had opened it.
      const promo = this.content.querySelector('[data-cart-promo]');
      const nextPromo = next.querySelector('[data-cart-promo]');
      if (promo && promo.open && nextPromo) nextPromo.open = true;

      this.content.replaceWith(next);
      this.revision += 1;
      const recovery = this.panel.querySelector('[data-cart-recovery]');
      if (recovery) recovery.hidden = true;
      this.syncBusy();

      const itemCount = Number(next.dataset.itemCount) || 0;
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: { item_count: itemCount } } }));

      if (focusIndex >= 0) {
        const nextControls = [...next.querySelectorAll(FOCUSABLE)];
        const target =
          nextControls[Math.min(focusIndex, nextControls.length - 1)] ||
          this.panel.querySelector('header [data-cart-drawer-close]');
        if (target) target.focus();
      }
      this.loadUpsells();
      return true;
    }

    // Re-renders the drawer through the Section Rendering API. The current page
    // URL is tried first (keeps locale and preview context); the storefront root
    // is the fallback, because some page URLs (long query strings, app-added
    // parameters) can make the section request fail on its own.
    async refresh() {
      const revision = this.revision;
      const sequence = ++this.refreshSequence;
      const pageUrl = new URL(pageContext(), window.location.origin);
      const candidates = [pageUrl.pathname + pageUrl.search, ROOT];
      for (const candidate of [...new Set(candidates)]) {
        const url = new URL(candidate, window.location.origin);
        url.searchParams.set('sections', SECTION_ID);
        let reply;
        try {
          reply = await fetchJson(url.pathname + url.search, {
            cache: 'no-store', headers: { Accept: 'application/json' },
          });
        } catch (error) {
          logIssue('section request failed', { url: url.pathname + url.search, error: String(error) });
          continue;
        }
        // An older GET must not overwrite a newer cart mutation or refresh.
        if (revision !== this.revision || sequence !== this.refreshSequence) return true;
        if (reply.ok && this.render(reply.json[SECTION_ID])) return true;
        logIssue('section unusable', { url: url.pathname + url.search, status: reply.status, hasSection: Boolean(reply.json[SECTION_ID]) });
      }
      throw new Error('Cart section unavailable');
    }

    // A busy storefront can briefly refuse cart requests (rate limiting); try a
    // few times before telling the shopper something went wrong.
    async refreshWithRetry() {
      for (const delay of [0, 800, 2000]) {
        if (delay) await wait(delay);
        try {
          await this.refresh();
          return true;
        } catch (_) {
          // Try again.
        }
      }
      return false;
    }

    // Setting a line's quantity is safe to repeat, so a refused or unreadable
    // reply is retried once. Returns null when both attempts failed.
    async changeLine(payload) {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const result = await postJson('cart/change.js', payload);
          if (result.status !== 429 && result.status < 500 && (result.ok || result.status >= 400)) return result;
        } catch (error) {
          logIssue('cart/change.js request failed', { error: String(error) });
        }
        if (attempt === 0) await wait(900);
      }
      return null;
    }

    // Mirrors Hydrogen's cart loader: complementary recommendations for the
    // first cart line, minus what is already in the cart; the server-rendered
    // fallback list stays when Shopify has none.
    async loadUpsells() {
      const box = this.content?.querySelector('[data-cart-upsells]');
      if (!box) return;
      const list = box.querySelector('[data-cart-upsells-list]');
      if (!list) return;
      const productId = box.dataset.productId;
      box.hidden = list.children.length === 0;
      if (!productId) return;

      const inCart = new Set(box.dataset.cartProductIds.split(','));
      try {
        if (!this.upsellCache.has(productId)) {
          const url = `${ROOT}recommendations/products?product_id=${productId}&limit=6&intent=complementary&section_id=${SECTION_ID}`;
          this.upsellCache.set(
            productId,
            fetch(url).then((response) => (response.ok ? response.text() : '')),
          );
        }
        const html = await this.upsellCache.get(productId);
        const items = [
          ...new DOMParser().parseFromString(html, 'text/html').querySelectorAll('[data-upsell-source] [data-upsell-product-id]'),
        ].filter((item) => !inCart.has(item.dataset.upsellProductId));
        if (items.length === 0 || !box.isConnected) return;
        list.replaceChildren(...items.slice(0, 3)); // same count as the Liquid fallback
        box.hidden = false;
      } catch (error) {
        this.upsellCache.delete(productId);
      }
    }

    async handleSubmit(event) {
      if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) return;
      const form = event.target;
      if (this.busy && (event.submitter?.name === 'checkout' || form.querySelector('[name="checkout"]'))) {
        event.preventDefault();
        return;
      }
      if (form.matches('[data-add-to-cart]')) return;
      const isChange = form.matches('[data-cart-change]');
      const isDiscount = form.matches('[data-cart-discount]');
      if (!isChange && !isDiscount) return;

      event.preventDefault();
      if (this.busy) return;
      const data = new FormData(form);
      if (!this.beginMutation()) return;
      try {
        if (isChange) {
          const result = await this.changeLine({
            id: data.get('id'),
            quantity: Number(data.get('quantity')),
            sections: SECTION_ID,
            sections_url: pageContext(),
          });
          if (!result) throw new Error('cart change failed');
          const { ok, json } = result;
          if (!ok) {
            // 422 (e.g. more than the available stock): re-sync the drawer, then show
            // Shopify's localized message instead of the generic network error.
            if (!(await this.refreshWithRetry())) this.showRecovery();
            this.showError(json.description || json.message || this.element.dataset.networkError);
            return;
          }
          // The change is confirmed from here on. Only the drawer markup may be missing
          // (e.g. an app rebuilt the request without `sections`).
          if (!this.render(json.sections && json.sections[SECTION_ID])) {
            if (!(await this.refreshWithRetry())) this.showConfirmedState();
          }
        } else {
          const code = String(data.get('discount') || '').trim();
          const { ok, json } = await postJson('cart/update.js', {
            discount: code,
            sections: SECTION_ID,
            sections_url: pageContext(),
          });
          if (!ok) throw new Error('discount update failed');
          if (!this.render(json.sections && json.sections[SECTION_ID]) && !(await this.refreshWithRetry())) {
            this.showConfirmedState();
            return;
          }
          const invalid = code !== '' && (json.discount_codes || []).some((entry) => !entry.applicable);
          const error = this.content.querySelector('[data-cart-discount-error]');
          if (error) {
            error.hidden = !invalid;
            const promo = error.closest('[data-cart-promo]');
            if (invalid && promo) promo.open = true;
          }
        }
      } catch (error) {
        if (!(await this.refreshWithRetry())) this.showRecovery();
        this.showError(this.element.dataset.networkError);
      } finally {
        this.endMutation();
      }
    }
  }

  const drawerElement = document.querySelector('[data-cart-drawer]');
  const drawer = drawerElement?.querySelector('aside') && drawerElement.querySelector('[data-cart-drawer-content]')
    ? new CartDrawer(drawerElement) : null;
  window.SeedCartDrawer = drawer;

  // Every quick-add and the PDP buy form. On /cart (no drawer) they stay plain POSTs.
  // Window bubbling runs AFTER form/document app handlers. Respect an app that
  // has already taken ownership, instead of submitting its bundle a second time.
  window.addEventListener('submit', async (event) => {
    if (event.defaultPrevented || !(event.target instanceof HTMLFormElement)) return;
    const form = event.target.closest('form[data-add-to-cart]');
    if (!form || !drawer || event.submitter?.name === 'checkout') return;

    event.preventDefault();
    const submitter = event.submitter || form.querySelector('[type="submit"]');
    if (drawer.busy) return;
    const body = new FormData(form);
    if (!drawer.beginMutation()) return;

    try {
      body.set('sections', SECTION_ID);
      body.set('sections_url', pageContext());
      const { ok, status, json } = await fetchJson(`${ROOT}cart/add.js`, {
        method: 'POST', headers: { Accept: 'application/json' }, body,
      });

      // 2xx with an unreadable body: the add went through, only the reply is unusable.
      if (!ok && status >= 200 && status < 300) {
        if (!(await drawer.refreshWithRetry())) {
          drawer.showConfirmedState();
          return;
        }
        drawer.open(submitter);
        return;
      }

      if (!ok) {
        // 422: sold out / over the available quantity. Shopify's message is localized.
        drawer.showError(json.description || json.message || '');
        drawer.open(submitter);
        return;
      }

      // The add is confirmed. If the drawer cannot show it, the cart page can.
      if (!drawer.render(json.sections && json.sections[SECTION_ID]) && !(await drawer.refreshWithRetry())) {
        drawer.showConfirmedState();
        return;
      }
      drawer.open(submitter);

      if (submitter && submitter.dataset.addedLabel) {
        submitter.textContent = submitter.dataset.addedLabel;
        setTimeout(() => {
          submitter.textContent = submitter.dataset.addLabel;
        }, ADDED_LABEL_MS);
      }
    } catch (error) {
      // The add may already have succeeded. Never replay a non-idempotent POST.
      if (!(await drawer.refreshWithRetry())) drawer.showRecovery();
      drawer.showError(drawer.element.dataset.networkError);
      drawer.open(submitter);
    } finally {
      drawer.endMutation();
    }
  });

  // Use the same primary button path for sticky buying, so app-specific click
  // handlers receive the selected bundle instead of a separate native submit.
  document.addEventListener('click', (event) => {
    const sticky = event.target.closest?.('.pdp-sticky-btn');
    if (!sticky || event.defaultPrevented || !sticky.form) return;
    const primary = sticky.form.querySelector('.pdp-atc-btn');
    if (!primary || primary === sticky) return;
    event.preventDefault();
    event.stopPropagation();
    if (!sticky.disabled && !primary.disabled) primary.click();
  }, true);

  window.addEventListener('pageshow', (event) => {
    if (event.persisted && drawer && !drawer.busy) drawer.refreshQuietly();
  });
})();